import { post } from '@citrite/http';
import { WorkspaceConfiguration } from '@citrite/workspace-ui-platform';
import { environment } from 'Environment';
import { getDefaultLeasingProperties } from 'Environment/ChromeApp/ChromeAppTelemetryLaunch';
import { ListenToChromeAppMessageType } from 'Environment/ChromeApp/types';
import { launchResource } from 'Environment/launchResource';
import { isCitrixChromeApp } from 'Environment/launchResource/device';
import { LaunchStatus } from 'Environment/launchResource/launchResource';
import { getLatestResourceStatus } from 'Environment/launchResource/launchUtil';
import { ChromeAppContext } from 'Workspace/ChromeApp/ChromeAppContext';
import { ResourceContext } from 'Workspace/ResourceProvider';
import { Resource } from 'Workspace/ResourceProvider/resourceTypes';
import { createMessageQueue } from './MessageQueue';

let chromeAppWindow: Window = null;
let chromeAppOrigin: string = null;
const fullDetails = 'Full';
let shieldEnabled = false;

interface QueuedMessage {
	data: any;
	origin: string;
}

interface LaunchStatusPayload {
	data: LaunchStatusData;
	correlationId: string;
}

interface LaunchStatusData {
	status: 'success' | 'failure';
	resourceId: string;
	errorId?: string;
	errorMessage?: string;
}

const messageQueue = createMessageQueue<QueuedMessage>();

export function initializeChromeApp() {
	return new Promise<void>(resolve => {
		if (isCitrixChromeApp()) {
			window.addEventListener(
				'message',
				event => {
					const { data, origin } = event;
					const messageIdentifier = data?.data || data?.messageType || data?.type;

					if (messageIdentifier) {
						postAcknowledgementToChromeApp(messageIdentifier, data?.correlationId);
					}

					if (data.data === 'offlineMode') {
						environment.setCitrixCloudConnectedStatus(false);
						return;
					}
					if (data.messageType === 'setCapabilities') {
						shieldEnabled = data.status === 'successful';
						return;
					}
					if (data.type === 'fetchIca') {
						if (!isChromeAppListenerReady()) {
							messageQueue.enqueue({ data, origin });
							return;
						}
						return;
					}
					if (data !== 'ICA') {
						return;
					}
					chromeAppWindow = event.source as Window;
					chromeAppOrigin = origin;
					postMessageToChromeApp({ type: 'HideAccountSettings' });
					resolve();
				},
				false
			);
		} else {
			resolve();
		}
	});
}

function isChromeAppListenerReady() {
	return messageQueue.isListenerReady();
}

export function shieldToggleFromChromeapp() {
	return shieldEnabled;
}

export function addChromeAppListener(
	getWorkspaceConfiguration: () => WorkspaceConfiguration,
	getResourceContext: () => ResourceContext,
	updateChromeAppContext: ChromeAppContext['updateChromeAppContext']
) {
	if (isCitrixChromeApp()) {
		window.addEventListener(
			'message',
			event => {
				const { data, origin } = event;
				const fromExpectedOrigin = chromeAppOrigin === origin;
				if (!fromExpectedOrigin || !data || (!data.type && !data.messageType)) {
					return;
				}
				const messageIdentifier = data?.type || data?.messageType;
				postAcknowledgementToChromeApp(messageIdentifier, data?.correlationId);
				receiveMessageFromChromeApp(
					data,
					getWorkspaceConfiguration(),
					getResourceContext(),
					updateChromeAppContext
				);
			},
			false
		);
		messageQueue.setReady();

		messageQueue.processAndClearAsync(queuedMessage => {
			if (queuedMessage.data?.type) {
				postAcknowledgementToChromeApp(
					queuedMessage.data.type,
					queuedMessage.data?.correlationId
				);
			}
			receiveMessageFromChromeApp(
				queuedMessage.data,
				getWorkspaceConfiguration(),
				getResourceContext(),
				updateChromeAppContext
			);
		});
	}
}

export interface ChromeAppLaunchOptions {
	/** This will be set when WSUI is instructed by the Chrome App to launch a resource */
	uniqueIdentifier: string;
}

function launchResourceForChromeApp(
	workspaceConfiguration: WorkspaceConfiguration,
	resourceContext: ResourceContext,
	resourceId: string,
	correlationId: string
) {
	const resource = resourceContext.resources.find(r => r.id === resourceId);
	if (!resource) {
		return;
	}
	const chromeAppOptions: ChromeAppLaunchOptions = {
		uniqueIdentifier: correlationId,
	};
	launchResource({
		resource,
		workspaceConfiguration,
		resourceContext,
		launchOptions: {
			isAutoLaunch: false,
			chromeAppOptions,
		},
	});
}

function receiveMessageFromChromeApp(
	data: any,
	workspaceConfiguration: WorkspaceConfiguration,
	resourceContext: ResourceContext,
	updateChromeAppContext: ChromeAppContext['updateChromeAppContext']
) {
	if (data.type === 'Settings') {
		postSettingsToChromeApp(workspaceConfiguration);
	} else if (data.type === 'LaunchApp') {
		launchResourceForChromeApp(
			workspaceConfiguration,
			resourceContext,
			data.appID,
			data.uniqueIdentifier
		);
	} else if (data.type === 'Resources') {
		chromeAppRequestResources(workspaceConfiguration.storeProxy.resourcesProxy.listURL);
	} else if (data.type === 'fetchIca') {
		launchResourceForChromeApp(
			workspaceConfiguration,
			resourceContext,
			data.resourceId,
			data.correlationId
		);
	} else if (data.messageType === ListenToChromeAppMessageType.INITIATE_STORE_RESP) {
		const leasingProperties = data?.payload?.leasingProperties?.properties;
		updateChromeAppContext({
			leasingProperties: leasingProperties
				? {
						...getDefaultLeasingProperties(),
						...leasingProperties,
				  }
				: null,
		});
	} else if (data.type === 'launchStatus') {
		handleLaunchStatusFromChromeApp(data.payload, resourceContext);
	}
}

export function postICADataErrorToChromeApp(
	errorMsg: string,
	resource: Resource,
	uniqueIdentifier: any
) {
	postMessageToChromeApp({
		type: 'ICAData',
		appID: resource.id,
		uniqueIdentifier,
		error: errorMsg,
	});
}

export function postICADataToChromeApp(
	icaData: any,
	resource: Resource,
	chromeAppOptions: ChromeAppLaunchOptions
) {
	if (chromeAppOptions) {
		postMessageToChromeApp({
			type: 'ICAData',
			appID: resource.id,
			uniqueIdentifier: chromeAppOptions.uniqueIdentifier,
			ICAFile: icaData,
		});
	} else {
		postMessageToChromeApp(icaData);
	}
}

function postAcknowledgementToChromeApp(messageType: string, correlationId?: string) {
	postMessageToChromeApp({
		type: `${messageType}Ack`,
		correlationId,
	});
}

export function postMessageToChromeApp(data: any) {
	if (chromeAppWindow && chromeAppOrigin) {
		chromeAppWindow.postMessage(data, chromeAppOrigin);
	}
}

function postSettingsToChromeApp(workspaceConfiguration: WorkspaceConfiguration) {
	postMessageToChromeApp({
		type: 'ChromeAppPreferences',
		data: workspaceConfiguration.pluginAssistant.html5.chromeAppPreferences,
	});
}

function chromeAppRequestResources(url: string) {
	const resourcesData = {
		format: 'json',
		resourceDetails: fullDetails,
	};

	post<any>(url, resourcesData)
		.then(data => {
			postResourcesToChromeApp(data.resources);
		})
		.catch(() => {
			postResourcesErrorToChromeApp('ResourcesFailed');
		});
}

export function postResourcesToChromeApp(resources: any) {
	postMessageToChromeApp({ type: 'ResourceData', resources });
}

function postResourcesErrorToChromeApp(error: string) {
	postMessageToChromeApp({ type: 'ResourceData', error });
}

function handleLaunchStatusFromChromeApp(
	payload: LaunchStatusPayload,
	resourceContext: ResourceContext
) {
	const { data, correlationId } = payload || {};
	if (!data?.status || !correlationId) {
		return;
	}
	const { status, resourceId, errorMessage } = data;

	const isSuccess = status === 'success';
	const launchStatus = isSuccess ? LaunchStatus.Completed : LaunchStatus.Failed;
	const launchError = isSuccess ? undefined : errorMessage;

	resourceContext.updateSession(state => {
		const updatedLaunchProgressEntry = {
			...state.launchProgress[correlationId],
			status: launchStatus,
			error: launchError,
			timeStamp: Date.now(),
		};

		const updatedLaunchProgress = {
			...state.launchProgress,
			[correlationId]: updatedLaunchProgressEntry,
		};

		return {
			launchInProgressIds: state.launchInProgressIds.filter(id => id !== resourceId),
			launchProgress: updatedLaunchProgress,
			resourceLatestStatus: getLatestResourceStatus(
				updatedLaunchProgress,
				state.resourceLatestStatus
			),
		};
	});
}

export function isTrusted(config: WorkspaceConfiguration) {
	if (!isCitrixChromeApp()) {
		return true;
	}
	const origins = (config.pluginAssistant.html5.chromeAppOrigins || '').split('|');
	return origins.includes(chromeAppOrigin);
}
